import { useMemo } from "react";

import { DestinationVendorsResponse, ExistingDestination, useDestinationVendors } from "@prequel/react";
import { Status, StatusLight, TableRow } from "@prequel/shared-components";

import { ApiRoutes } from "@/api/client";
import { useAllDestinations, useDestinations } from "@/api/queries/destinations";
import { useAllModels } from "@/api/queries/models";
import { useAllRecipients } from "@/api/queries/recipients";
import ResourceTable, { type FilterField } from "@/components/ResourceTable";
import Timestamp from "@/components/Timestamp";
import VendorLogo from "@/components/VendorLogo";
import { env } from "@/env";
import { getReadableLastSuccessfulSync } from "@/types";
import { getReadableVendor, getVendorLogoUrl } from "@/types/common";

const DESTINATION_TABLE_COLUMNS = ["Destination Name", "Vendor", "Last Successful Sync", "Status", "Created at"];

const renderVendorCell = (vendor: string, vendors: DestinationVendorsResponse) => (
  <div className="flex items-center">
    <VendorLogo logo_url={getVendorLogoUrl(vendor, vendors ?? [])} />
    {getReadableVendor(vendor, vendors ?? [])}
  </div>
);

const mapDestinationToRow = (
  destination: ExistingDestination,
  destinationVendors: DestinationVendorsResponse,
): TableRow => ({
  id: destination.id,
  to: `${ApiRoutes.DESTINATIONS}/${destination.id}`,
  cells: [
    { value: destination.name },
    { value: renderVendorCell(destination.vendor, destinationVendors) },
    {
      value: (
        <div className="text-gray-500">
          {getReadableLastSuccessfulSync(destination.last_successful_transfer_ended_at ?? undefined)}
        </div>
      ),
    },
    {
      value: (
        <div className="flex items-center text-gray-500">
          <StatusLight status={destination.is_enabled ? Status.ACTIVE : Status.INACTIVE} />
          {destination.is_enabled ? "Enabled" : "Disabled"}
        </div>
      ),
    },
    { value: <Timestamp timestamp={destination.created_at} /> },
  ],
});

const STATUS_OPTIONS = [
  { value: "true", label: "Enabled" },
  { value: "false", label: "Disabled" },
];

const DestinationsTable = () => {
  const destinationVendors = useDestinationVendors(env.VITE_APP_API_SERVER);
  const { data: allDestinations, isLoading: destinationsLoading } = useAllDestinations();
  const { data: allModels, isLoading: modelsLoading } = useAllModels();
  const { data: allRecipients, isLoading: recipientsLoading } = useAllRecipients();

  const rowMapper = useMemo(
    () => (destination: ExistingDestination) => mapDestinationToRow(destination, destinationVendors),
    [destinationVendors],
  );

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        key: "destination_id",
        label: "Destination ID",
        queryParam: "destination_id",
        loading: destinationsLoading,
        options: (allDestinations ?? [])
          .map((d) => ({ value: d.id, label: d.id }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        key: "id_in_provider_system",
        label: "ID in Provider System",
        queryParam: "id_in_provider_system",
        loading: recipientsLoading,
        options: (allRecipients ?? [])
          .map((r) => ({ value: r.id_in_provider_system, label: r.id_in_provider_system }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        key: "name",
        label: "Name",
        queryParam: "name",
        loading: destinationsLoading,
        options: (allDestinations ?? [])
          .map((d) => ({ value: d.name, label: d.name }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
      {
        key: "is_enabled",
        label: "Status",
        queryParam: "is_enabled",
        radio: true,
        options: STATUS_OPTIONS,
      },
      {
        key: "vendor",
        label: "Vendor",
        queryParam: "vendor",
        options: (destinationVendors ?? []).map((v) => ({ value: String(v.key), label: v.display })),
      },
      {
        key: "model_name",
        label: "Model",
        queryParam: "model_name",
        loading: modelsLoading,
        options: (allModels ?? [])
          .map((m) => ({ value: m.model_name, label: m.model_name }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      },
    ],
    [
      allDestinations,
      destinationsLoading,
      allModels,
      modelsLoading,
      allRecipients,
      recipientsLoading,
      destinationVendors,
    ],
  );

  return (
    <ResourceTable<ExistingDestination>
      columns={DESTINATION_TABLE_COLUMNS}
      useQuery={useDestinations}
      rowMapper={rowMapper}
      emptyMessage="No destinations found. Destinations will show up here."
      filterFields={filterFields}
    />
  );
};

export default DestinationsTable;
